import React, { useEffect, useState } from 'react';
import apiService from '../services/apiService';

// Tela interna: onde baixar os apps do Aliado — o Android (Google Play) e o instalador do
// app desktop (Windows). É o endereço para DIVULGAR: o link do instalador é privado e vale
// ~60 s, então ele só é gerado no clique, pela rota do Express.
//
// Cada admin aponta para o canal do seu ambiente (admin-qa → QA, admin-hmg → HMG, admin →
// produção), o mesmo em que o CI do aliado-recorder-desktop publica.
//
// A guarda NÃO é `role === 'admin'` (há contas de CLIENTE com esse papel): quem tranca é o
// `requireInterno` na rota — o instalador leva os tokens da API compilados.

const CANAL = { qa: 'QA', hmg: 'Homologação', prod: 'Produção' };

// Timestamp com fuso (o pub_date do latest.json vem em UTC com Z): mostrado em Brasília.
function fmtData(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
                                     year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Cartao({ icone, titulo, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e7ee', borderRadius: 12,
                  padding: '20px 22px', flex: '1 1 340px', maxWidth: 520 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 26 }} aria-hidden>{icone}</span>
        <h2 style={{ margin: 0, fontSize: 17, color: '#2a2a32' }}>{titulo}</h2>
      </div>
      {children}
    </div>
  );
}

const botao = {
  display: 'inline-block', background: '#6d28d9', color: '#fff', border: 0, borderRadius: 8,
  padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
};
const nota = { fontSize: 13, color: '#6b6a78', margin: '10px 0 0', lineHeight: 1.5 };

function Copiar({ texto, rotulo }) {
  const [ok, setOk] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setOk(true);
      setTimeout(() => setOk(false), 2000);
    } catch { /* sem permissão de área de transferência: o texto está na tela */ }
  };
  return (
    <button type="button" onClick={copiar}
            style={{ ...botao, background: '#f2f2f5', color: '#44434d', fontWeight: 500, marginLeft: 8 }}>
      {ok ? 'Copiado ✓' : rotulo}
    </button>
  );
}

function Desktop({ d, paginaUrl }) {
  const [baixando, setBaixando] = useState(false);
  const [erroLink, setErroLink] = useState(null);
  const publicado = fmtData(d.publicadoEm);

  const baixar = async () => {
    setErroLink(null);
    setBaixando(true);
    const r = await apiService.getLinkInstaladorDesktop(d.canal);
    setBaixando(false);
    if (r.success) window.location.href = r.data.url;
    else setErroLink(r.message);
  };

  return (
    <Cartao icone="💻" titulo={`Aliado para Windows (desktop) · ${CANAL[d.canal] || d.canal}`}>
      {d.erro ? (
        <p style={{ ...nota, color: '#b3261e', marginTop: 0 }}>{d.erro}</p>
      ) : (
        <>
          <div style={{ fontSize: 14, color: '#2a2a32', marginBottom: 12 }}>
            Versão <strong>{d.versao}</strong>
            {publicado && <span style={{ color: '#6b6a78' }}> · publicada em {publicado}</span>}
          </div>
          <button type="button" onClick={baixar} disabled={baixando}
                  style={{ ...botao, opacity: baixando ? 0.6 : 1 }}>
            {baixando ? 'Gerando o link…' : 'Baixar instalador'}
          </button>
          <Copiar texto={paginaUrl} rotulo="Copiar link desta página" />
          {erroLink && <p style={{ ...nota, color: '#b3261e' }}>{erroLink}</p>}
          <p style={nota}>
            {d.arquivo ? <>Arquivo: {d.arquivo}. </> : null}
            {d.canal === 'prod'
              ? 'É a versão oficial: a que as lojas usam.'
              : 'É a versão interna, para testar antes de ir para a produção; o app dela conversa com o ambiente de teste.'}
          </p>
        </>
      )}
    </Cartao>
  );
}

export default function Apps() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    apiService.getApps().then((r) => (r.success ? setDados(r.data) : setErro(r.message)));
  }, []);

  if (erro) return <div style={{ padding: 32, color: '#b3261e' }}>{erro}</div>;
  if (!dados) return <div style={{ padding: 32, color: '#6b6a78' }}>Carregando…</div>;

  const { android } = dados;
  // Servidor antigo (sem desktopCanais): cai no canal único de antes.
  const canais = dados.desktopCanais || [dados.desktop];
  const paginaUrl = `${window.location.origin}/apps`;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100 }}>
      <h1 style={{ margin: 0, fontSize: 24, color: '#2a2a32' }}>Apps</h1>
      <p style={{ ...nota, margin: '6px 0 22px' }}>
        Onde baixar os apps do Aliado. Para divulgar, mande o endereço desta página
        ({paginaUrl}): o link do instalador desktop é gerado na hora e vale só um minuto.
        Depois de instalado, o app se atualiza sozinho. Enquanto o instalador não tiver assinatura
        digital, o Windows mostra &quot;O Windows protegeu o computador&quot;: clique em &quot;Mais
        informações&quot; → &quot;Executar assim mesmo&quot;.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
        <Cartao icone="🤖" titulo="Aliado para Android">
          <div>
            <a href={android.url} target="_blank" rel="noopener noreferrer" style={botao}>Abrir na Google Play</a>
            <Copiar texto={android.url} rotulo="Copiar link" />
          </div>
          <p style={nota}>{android.url}</p>
        </Cartao>

        {canais.map((d) => <Desktop key={d.canal} d={d} paginaUrl={paginaUrl} />)}
      </div>
    </div>
  );
}
